<template>
  <section class="audience-picker dm-profiles">
    <p class="dm-profiles__intro">
      <i class="fa-regular fa-paper-plane" aria-hidden="true"></i>
      <span>
        <strong>DM creators on their socials.</strong>
        Write one short DM, copy it, then open each profile below and send it from your own account. Each profile you open is ticked here.
      </span>
    </p>

    <!--
      Submitted with the step as well: Save and Mark as done write what is in
      the box, and leaving the box writes it on its own in between.
    -->
    <div class="dm-profiles__compose">
      <div class="dm-profiles__compose-head">
        <label class="dm-profiles__label" for="dm_body">DM message</label>
        <span class="dm-profiles__save" role="status">{{ saveState }}</span>
      </div>
      <textarea
        id="dm_body"
        v-model="body"
        name="dm_body"
        class="dm-profiles__body"
        rows="4"
        :maxlength="maxLength"
        placeholder="Write a short DM for the selected creators&hellip;"
        @blur="save"
      ></textarea>
      <div class="dm-profiles__compose-foot">
        <span class="audience-picker__meta">{{ body.length }} / {{ maxLength }}</span>
        <button type="button" class="review-btn review-btn--secondary dm-profiles__copy" :disabled="!body.trim()" @click="copy">
          <i :class="copied ? 'fa-solid fa-check' : 'fa-regular fa-copy'" aria-hidden="true"></i>
          <span>{{ copied ? 'Copied' : 'Copy' }}</span>
        </button>
      </div>
    </div>

    <header class="audience-picker__head">
      <label class="audience-picker__search dm-profiles__search">
        <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
        <input
          type="search"
          v-model="creators.search"
          @keydown.enter.prevent
          placeholder="Search creators by name, email, or handle"
          aria-label="Search creators by name, email, or handle"
        >
      </label>

      <!-- The schedule step's checkbox, markup and all, so the section has one. -->
      <label class="marketing-choice dm-profiles__filter">
        <input type="checkbox" v-model="unopened">
        <span class="marketing-check" aria-hidden="true"><i class="fa-solid fa-check"></i></span>
        <span>Not opened yet</span>
      </label>

      <p class="audience-picker__summary" role="status">{{ audience }}</p>
    </header>

    <p v-if="creators.error && !creators.items.length" class="audience-picker__empty">{{ creators.error }}</p>
    <p v-else-if="creators.loading && !creators.items.length" class="audience-picker__empty">Loading creators&hellip;</p>
    <p v-else-if="creators.isEmpty" class="audience-picker__empty">
      {{ creators.search || unopened ? 'No creator matches.' : 'No creator in this audience has a connected social profile.' }}
    </p>

    <ul v-else :ref="creators.observe" class="audience-picker__list dm-profiles__list">
      <li
        v-for="(creator, index) in creators.items"
        :key="creator.id"
        class="dm-profiles__creator"
        :data-load-more="index === creators.triggerIndex ? '' : null"
      >
        <div class="dm-profiles__who">
          <span class="audience-picker__avatar" :class="`audience-picker__avatar--tone-${creator.tone}`" aria-hidden="true">
            <img v-if="creator.avatar" :src="creator.avatar" alt="" loading="lazy" @error="creator.avatar = ''">
            <template v-else>{{ creator.initials }}</template>
          </span>
          <span class="audience-picker__identity">
            <span class="audience-picker__name">{{ creator.name || 'Unnamed creator' }}</span>
            <span class="audience-picker__meta">{{ creator.email }}</span>
          </span>
          <span class="dm-profiles__count">
            {{ openedCount(creator) }} of {{ creator.profiles.length }} opened
          </span>
        </div>

        <p v-if="!creator.profiles.length" class="audience-picker__meta dm-profiles__none">
          No profile link available.
        </p>

        <!--
          A real link, opened in a new tab by the browser itself: a popup opened
          from script after a request would be blocked. The tick is recorded
          alongside, and never holds the link up.
        -->
        <div v-else class="dm-profiles__grid">
          <a
            v-for="profile in creator.profiles"
            :key="profile.platform"
            :href="profile.url"
            target="_blank"
            rel="noopener noreferrer"
            class="dm-profiles__profile"
            :class="{ 'is-opened': profile.opened }"
            @click="markOpened(creator, profile)"
            @auxclick="markOpened(creator, profile)"
          >
            <i :class="icons[profile.platform]" class="dm-profiles__icon" aria-hidden="true"></i>
            <span class="dm-profiles__handle">
              {{ profile.label }}
              <small v-if="!profile.dm">No DMs on YouTube &mdash; opens the channel</small>
            </span>
            <span class="dm-profiles__action">
              <template v-if="profile.opened"><i class="fa-solid fa-check" aria-hidden="true"></i> Opened</template>
              <template v-else>Open</template>
            </span>
          </a>
        </div>
      </li>
    </ul>

    <footer v-if="creators.items.length" class="audience-picker__more">
      <span v-if="creators.loading">Loading more&hellip;</span>
      <template v-else-if="creators.needsManualLoad">
        <span v-if="creators.error" class="audience-picker__more-error">{{ creators.error }}</span>
        <button type="button" @click="creators.loadMore">{{ creators.error ? 'Try again' : 'Load more' }}</button>
      </template>
      <span v-else-if="creators.cursor">Scroll for more</span>
      <span v-else>{{ creators.items.length.toLocaleString() }} {{ creators.items.length === 1 ? 'creator' : 'creators' }}</span>
    </footer>
  </section>
</template>

<script>
import { ref, watch } from 'vue'
import { requestJson, usePagedList } from './paged-list.mjs'

/**
 * The DM tab of the message step: the campaign's creators and the social
 * profiles the client opens to message each of them by hand.
 *
 * Nothing is sent from here. What this list keeps is the client's progress -
 * which profiles they have opened - so a long audience can be worked through
 * over several sittings.
 */
export default {
  name: 'DmProfiles',

  props: {
    // Endpoint returning { items: [{ id, name, email, profiles: [...] }], next_cursor }.
    url: {
      type: String,
      required: true
    },
    // Endpoint recording that one profile was opened.
    openUrl: {
      type: String,
      required: true
    },
    // Endpoint saving the DM on blur.
    bodyUrl: {
      type: String,
      required: true
    },
    initialBody: {
      type: String,
      required: false,
      default: ''
    },
    maxLength: {
      type: Number,
      required: true
    },
    // The "who is this for" line, worked out on the server.
    audience: {
      type: String,
      required: false,
      default: ''
    }
  },

  setup(props) {
    const unopened = ref(false)

    const creators = usePagedList({
      url: props.url,
      method: 'POST',
      params: () => ({ unopened: unopened.value })
    })

    watch(unopened, () => creators.reload())

    creators.reload()

    const icons = {
      instagram: 'fa-brands fa-instagram',
      tiktok: 'fa-brands fa-tiktok',
      twitter: 'fa-brands fa-x-twitter',
      facebook: 'fa-brands fa-facebook',
      youtube: 'fa-brands fa-youtube',
      pinterest: 'fa-brands fa-pinterest'
    }

    const openedCount = (creator) => creator.profiles.filter((profile) => profile.opened).length

    const markOpened = async (creator, profile) => {
      if (profile.opened) return

      profile.opened = true

      try {
        await requestJson(props.openUrl, 'POST', { creator_id: creator.id, platform: profile.platform })
      } catch (e) {
        // The profile did open; only the tick failed to save. Shown as not
        // opened, so the list does not claim progress it has not kept.
        profile.opened = false
      }
    }

    const body = ref(props.initialBody)
    const saveState = ref('')
    const copied = ref(false)

    // What the server last accepted, so leaving the box without a change
    // does not write the same words again.
    let saved = props.initialBody

    const save = async () => {
      const value = body.value
      if (value === saved) return

      saveState.value = 'Saving…'

      try {
        await requestJson(props.bodyUrl, 'POST', { dm_body: value })
        saved = value
        // A later edit may already be on its way; only the newest says Saved.
        if (body.value === value) saveState.value = 'Saved'
      } catch (e) {
        saveState.value = e.fromServer ? e.message : 'Not saved. Check your connection.'
      }
    }

    let copiedTimer = null

    const copy = async () => {
      clearTimeout(copiedTimer)

      try {
        await navigator.clipboard.writeText(body.value)
        copied.value = true
        copiedTimer = setTimeout(() => { copied.value = false }, 2000)
      } catch (e) {
        // The Clipboard API needs a secure page and a browser that has it.
        // Without it the text is selected, and the client copies it by hand -
        // execCommand('copy') is deprecated and not worth leaning on.
        const box = document.getElementById('dm_body')
        box.focus()
        box.select()
      }
    }

    return { creators, unopened, icons, openedCount, markOpened, body, saveState, copied, save, copy }
  }
}
</script>
